import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';

import PublicLayout from '@/layouts/PublicLayout';
import PortalLayout from '@/layouts/PortalLayout';

import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import ForgotPassword from '@/pages/ForgotPassword';
import SetPassword from '@/pages/SetPassword';

import Dashboard from '@/pages/portal/Dashboard';
import CandidatesList from '@/pages/portal/CandidatesList';
import CandidateNew from '@/pages/portal/CandidateNew';
import CandidateDetail from '@/pages/portal/CandidateDetail';
import ActivityCenter from '@/pages/portal/ActivityCenter';
import NotificationsCenter from '@/pages/portal/NotificationsCenter';
import ResumeEnrichment from '@/pages/portal/ResumeEnrichment';
import ResumeEnrichmentWorkspace from '@/pages/portal/ResumeEnrichmentWorkspace';

import UsersSettings from '@/pages/portal/settings/UsersSettings';
import RolesSettings from '@/pages/portal/settings/RolesSettings';
import PipelinesSettings from '@/pages/portal/settings/PipelinesSettings';
import VideoBotBank from '@/pages/portal/settings/VideoBotBank';
import McqBank from '@/pages/portal/settings/McqBank';
import TechAssessmentBank from '@/pages/portal/settings/TechAssessmentBank';
import RbacMatrix from '@/pages/portal/settings/RbacMatrix';
import PanelBank from '@/pages/portal/settings/PanelBank';
import OfferBank from '@/pages/portal/settings/OfferBank';
import AiReportConfig from '@/pages/portal/settings/AiReportConfig';
import ResumeTemplateSettings from '@/pages/portal/settings/ResumeTemplateSettings';
import EmailTemplates from '@/pages/portal/settings/EmailTemplates';
import GlobalSettings from '@/pages/portal/settings/GlobalSettings';
import CandidateLanding from '@/pages/candidate/CandidateLanding';
import VideoBotSession from '@/pages/candidate/VideoBotSession';
import McqSession from '@/pages/candidate/McqSession';
import CodeSession from '@/pages/candidate/CodeSession';
import TechAssessmentSession from '@/pages/candidate/TechAssessmentSession';
import SebQuit from '@/pages/candidate/SebQuit';

import ClientGate from '@/pages/client/ClientGate';
import ClientReport from '@/pages/client/ClientReport';
import ClientReportDemo from '@/pages/client/ClientReportDemo';

import PanelFeedback from '@/pages/panel/PanelFeedback';
import PanelResume from '@/pages/panel/PanelResume';

import { AuthProvider } from '@/lib/auth';
import RequireAuth from '@/components/RequireAuth';
import { ErrorBoundary } from '@/components/ErrorBoundary';

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ErrorBoundary>
          <Routes>
          {/* Public */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/set-password" element={<SetPassword />} />
          </Route>
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Candidate (OTC) flow — minimal chrome */}
          <Route path="/candidate/:otc" element={<CandidateLanding />} />
          <Route path="/candidate/:otc/videobot" element={<VideoBotSession />} />
          <Route path="/candidate/:otc/mcq" element={<McqSession />} />
          <Route path="/candidate/:otc/tech-assessment" element={<TechAssessmentSession />} />
          <Route path="/candidate/:otc/code" element={<CodeSession />} />
          <Route path="/seb-quit" element={<SebQuit />} />

          {/* Panellist feedback — standalone public token-gated form */}
          <Route path="/panel-feedback/:token" element={<PanelFeedback />} />
          {/* Panellist briefing — private parsed CV view */}
          <Route path="/panel-resume/:token" element={<PanelResume />} />

          {/* Client report — standalone, branded */}
          <Route path="/client/:token" element={<ClientGate />} />
          <Route path="/client/:token/report" element={<ClientReport />} />
          {/* Sprint 11.12 — Hardcoded magazine baseline demo (no auth, no gate). */}
          <Route path="/client-report-demo" element={<ClientReportDemo />} />

          {/* HR / Admin Portal (SSO-protected) */}
          <Route
            path="/portal"
            element={
              <RequireAuth>
                <PortalLayout />
              </RequireAuth>
            }
          >
            <Route
              index
              element={<RequireAuth need="dashboard.view"><Dashboard /></RequireAuth>}
            />
            <Route
              path="candidates"
              element={(
                <RequireAuth anyOf={['candidate.view_all', 'candidate.view_assigned']}>
                  <CandidatesList />
                </RequireAuth>
              )}
            />
            <Route
              path="candidates/new"
              element={(
                <RequireAuth anyOf={['candidate.view_all', 'candidate.view_assigned']}>
                  <CandidateNew />
                </RequireAuth>
              )}
            />
            <Route
              path="candidates/:id"
              element={(
                <RequireAuth anyOf={['candidate.view_all', 'candidate.view_assigned']}>
                  <CandidateDetail />
                </RequireAuth>
              )}
            />
            <Route
              path="notifications"
              element={<RequireAuth need="notifications.view"><NotificationsCenter /></RequireAuth>}
            />
            <Route path="activity" element={<ActivityCenter />} />
            <Route
              path="resume-enrichment"
              element={<RequireAuth need="candidate.bulk_enrichment"><ResumeEnrichment /></RequireAuth>}
            />
            <Route
              path="resume-enrichment/:candidateId"
              element={<RequireAuth need="candidate.bulk_enrichment"><ResumeEnrichmentWorkspace /></RequireAuth>}
            />
            <Route
              path="settings/users"
              element={<RequireAuth need="admin.users.manage"><UsersSettings /></RequireAuth>}
            />
            <Route
              path="settings/roles"
              element={<RequireAuth need="admin.master_data.manage"><RolesSettings /></RequireAuth>}
            />
            <Route
              path="settings/pipelines"
              element={<RequireAuth need="pipeline.view"><PipelinesSettings /></RequireAuth>}
            />
            <Route
              path="settings/questions/videobot"
              element={<RequireAuth need="bank.videobot.view"><VideoBotBank /></RequireAuth>}
            />
            <Route
              path="settings/questions/mcq"
              element={<RequireAuth need="bank.psa.view"><McqBank /></RequireAuth>}
            />
            <Route
              path="settings/questions/tech"
              element={<RequireAuth need="bank.tech.view"><TechAssessmentBank /></RequireAuth>}
            />
            <Route
              path="settings/rbac"
              element={(
                <RequireAuth anyOf={['admin.rbac.view', 'admin.rbac.manage']}>
                  <RbacMatrix />
                </RequireAuth>
              )}
            />
            <Route
              path="settings/questions/panel"
              element={<RequireAuth need="bank.panel.view"><PanelBank /></RequireAuth>}
            />
            <Route
              path="settings/questions/offer"
              element={<RequireAuth need="bank.offer.view"><OfferBank /></RequireAuth>}
            />
            <Route
              path="settings/ai-config"
              element={<RequireAuth need="admin.ai_config.manage"><AiReportConfig /></RequireAuth>}
            />
            <Route
              path="settings/resume-template"
              element={<RequireAuth need="admin.resume_template"><ResumeTemplateSettings /></RequireAuth>}
            />
            <Route
              path="settings/email-templates"
              element={<RequireAuth need="admin.email_templates"><EmailTemplates /></RequireAuth>}
            />
            <Route
              path="settings/global"
              element={<RequireAuth need="admin.global_settings"><GlobalSettings /></RequireAuth>}
            />
          </Route>

          <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </ErrorBoundary>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}
